import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Divider,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  Stack,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { IContributionTransaction } from "interfaces/contribution-transaction";
import { axiosInstance } from "providers/rest-data-provider/utils";
import awsExports from "aws-exports";

const API_BASE_URL = awsExports.endpoints[0].endpoint;

interface SageCustomer {
  customerId: string;
  name: string;
}

interface SageCustomerDetail extends SageCustomer {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
}

interface CreateCustomerForm {
  customerName: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  country: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
}

type Step =
  | { name: "suggestion"; customerId: string }
  | { name: "list" }
  | { name: "confirm"; customer: SageCustomerDetail }
  | { name: "create" };

const EMPTY_CREATE_FORM: CreateCustomerForm = {
  customerName: "",
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  country: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  zip: "",
};

interface Props {
  open: boolean;
  onClose: () => void;
  contribution: IContributionTransaction | undefined;
  onSaved: () => void;
}

const errorMessage = (error: any, fallback: string): string =>
  error?.response?.data?.message ?? error?.message ?? fallback;

const SelectSageCustomerModal: React.FC<Props> = ({
  open,
  onClose,
  contribution,
  onSaved,
}) => {
  const [step, setStep] = useState<Step>({ name: "list" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [search, setSearch] = useState("");
  const [customers, setCustomers] = useState<SageCustomer[]>([]);
  const [createForm, setCreateForm] =
    useState<CreateCustomerForm>(EMPTY_CREATE_FORM);

  const contributionId = contribution?.contributionId;

  const fetchCustomers = async () => {
    setLoading(true);
    setError(undefined);
    try {
      const response = await axiosInstance.get(
        `${API_BASE_URL}/admin/sage-intacct/customers`
      );
      if (response.data.success) {
        setCustomers(
          (response.data.customers ?? []).map((c: any) => ({
            customerId: c.CUSTOMERID,
            name: c.NAME,
          }))
        );
      } else {
        setError(response.data.message ?? "Failed to load Sage Intacct customers.");
      }
    } catch (e: any) {
      setError(errorMessage(e, "Failed to load Sage Intacct customers."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !contributionId) return;
    setSearch("");
    setError(undefined);

    const fetchSuggestion = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get(
          `${API_BASE_URL}/admin/contribution-transactions/${contributionId}/suggested-customer`
        );
        if (response.data.success && response.data.customerId) {
          setStep({ name: "suggestion", customerId: response.data.customerId });
          setLoading(false);
        } else {
          setStep({ name: "list" });
          await fetchCustomers();
        }
      } catch (e: any) {
        setError(errorMessage(e, "Failed to look up a suggested customer."));
        setStep({ name: "list" });
        await fetchCustomers();
      }
    };

    fetchSuggestion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contributionId]);

  const filteredCustomers = useMemo(() => {
    if (!search.trim()) return customers;
    const term = search.trim().toLowerCase();
    return customers.filter((c) => c.name.toLowerCase().includes(term));
  }, [customers, search]);

  const viewCustomer = async (customerId: string) => {
    setLoading(true);
    setError(undefined);
    try {
      const response = await axiosInstance.get(
        `${API_BASE_URL}/admin/sage-intacct/customers/${customerId}`
      );
      if (response.data.success && response.data.customer) {
        const c = response.data.customer;
        setStep({
          name: "confirm",
          customer: {
            customerId: c.CUSTOMERID,
            name: c.NAME,
            firstName: c.FIRSTNAME,
            lastName: c.LASTNAME,
            email: c.EMAIL1,
            phone: c.PHONE1,
            addressLine1: c.ADDRESS1,
            addressLine2: c.ADDRESS2,
            city: c.CITY,
            state: c.STATE,
            zip: c.ZIP,
            country: c.COUNTRY,
          },
        });
      } else {
        setError(response.data.message ?? "Failed to load customer details.");
      }
    } catch (e: any) {
      setError(errorMessage(e, "Failed to load customer details."));
    } finally {
      setLoading(false);
    }
  };

  const backToList = () => {
    setError(undefined);
    setStep({ name: "list" });
    if (customers.length === 0) fetchCustomers();
  };

  const saveCustomer = async (customerId: string) => {
    if (!contributionId) return;
    setLoading(true);
    setError(undefined);
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}/admin/contribution-transactions/${contributionId}/select-customer`,
        { customerId }
      );
      if (response.data.success) {
        onSaved();
        onClose();
      } else {
        setError(response.data.message ?? "Failed to save customer.");
      }
    } catch (e: any) {
      setError(errorMessage(e, "Failed to save customer."));
    } finally {
      setLoading(false);
    }
  };

  const openCreateForm = () => {
    const report = contribution?.contributionReport;
    setError(undefined);
    setCreateForm({
      ...EMPTY_CREATE_FORM,
      customerName: report?.name ?? "",
      email: report?.donorEmail ?? "",
      addressLine1: report?.addressLine1 ?? "",
      addressLine2: report?.addressLine2 ?? "",
      city: report?.city ?? "",
      state: report?.state ?? "",
      zip: report?.zip ?? "",
    });
    setStep({ name: "create" });
  };

  const createCustomer = async () => {
    if (!contributionId) return;
    setLoading(true);
    setError(undefined);
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}/admin/contribution-transactions/${contributionId}/create-customer`,
        createForm
      );
      if (response.data.success) {
        onSaved();
        onClose();
      } else {
        setError(response.data.message ?? "Failed to create customer.");
      }
    } catch (e: any) {
      setError(errorMessage(e, "Failed to create customer."));
    } finally {
      setLoading(false);
    }
  };

  const title =
    step.name === "suggestion"
      ? "Suggested Customer"
      : step.name === "confirm"
      ? "Confirm Customer"
      : step.name === "create"
      ? "Create New Customer"
      : "Select Customer";

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="select-sage-customer-title">
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "90%", sm: 600, md: 700 },
          maxHeight: "90vh",
          overflow: "hidden",
          outline: "none",
        }}
      >
        <Paper
          elevation={24}
          sx={{ display: "flex", flexDirection: "column", height: "100%", maxHeight: "90vh" }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 3,
              pb: 2,
            }}
          >
            <Typography
              id="select-sage-customer-title"
              variant="h5"
              component="h2"
              fontWeight="medium"
            >
              {title}
            </Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider />

          <Box sx={{ p: 3, flex: 1, overflow: "auto" }}>
            {error && (
              <Typography color="error" sx={{ mb: 2 }}>
                {error}
              </Typography>
            )}

            {loading && (
              <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
                <CircularProgress />
              </Box>
            )}

            {!loading && step.name === "suggestion" && (
              <Stack spacing={2}>
                <Typography>
                  Based on the last contribution for this fund and donor, we
                  suggest the following Sage Intacct customer:
                </Typography>
                <TextField
                  fullWidth
                  disabled
                  label="Suggested Customer ID"
                  value={step.customerId}
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>
            )}

            {!loading && step.name === "list" && (
              <Stack spacing={2}>
                <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                  <TextField
                    fullWidth
                    placeholder="Search customer by name..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  <Button
                    variant="contained"
                    onClick={openCreateForm}
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    Create New Customer
                  </Button>
                </Box>
                <Box sx={{ maxHeight: 360, overflow: "auto" }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Customer ID</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell align="right">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredCustomers.map((c) => (
                        <TableRow key={c.customerId}>
                          <TableCell>{c.customerId}</TableCell>
                          <TableCell>{c.name}</TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => viewCustomer(c.customerId)}
                            >
                              Select
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {filteredCustomers.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={3}>
                            <Typography color="text.secondary" sx={{ py: 2 }}>
                              No customers found.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Box>
              </Stack>
            )}

            {!loading && step.name === "confirm" && (
              <Stack spacing={2}>
                <Typography>
                  Is this the customer you'd like to select?
                </Typography>
                <TextField
                  fullWidth
                  disabled
                  label="Customer ID"
                  value={step.customer.customerId}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  fullWidth
                  disabled
                  label="Name"
                  value={step.customer.name}
                  InputLabelProps={{ shrink: true }}
                />
                {(step.customer.firstName || step.customer.lastName) && (
                  <TextField
                    fullWidth
                    disabled
                    label="Contact Name"
                    value={`${step.customer.firstName ?? ""} ${
                      step.customer.lastName ?? ""
                    }`.trim()}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
                {step.customer.email && (
                  <TextField
                    fullWidth
                    disabled
                    label="Email"
                    value={step.customer.email}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
                {step.customer.phone && (
                  <TextField
                    fullWidth
                    disabled
                    label="Phone"
                    value={step.customer.phone}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
                {(step.customer.addressLine1 || step.customer.city) && (
                  <TextField
                    fullWidth
                    disabled
                    label="Address"
                    value={[
                      step.customer.addressLine1,
                      step.customer.addressLine2,
                      step.customer.city,
                      step.customer.state,
                      step.customer.zip,
                      step.customer.country,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              </Stack>
            )}

            {!loading && step.name === "create" && (
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  required
                  label="Customer Name"
                  value={createForm.customerName}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, customerName: e.target.value })
                  }
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  fullWidth
                  label="First Name"
                  value={createForm.firstName}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, firstName: e.target.value })
                  }
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  fullWidth
                  label="Last Name"
                  value={createForm.lastName}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, lastName: e.target.value })
                  }
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  fullWidth
                  label="Email Address"
                  value={createForm.email}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, email: e.target.value })
                  }
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  fullWidth
                  label="Primary Phone"
                  value={createForm.phone}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, phone: e.target.value })
                  }
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  fullWidth
                  label="Country"
                  value={createForm.country}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, country: e.target.value })
                  }
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  fullWidth
                  label="Address Line 1"
                  value={createForm.addressLine1}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, addressLine1: e.target.value })
                  }
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  fullWidth
                  label="Address Line 2"
                  value={createForm.addressLine2}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, addressLine2: e.target.value })
                  }
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  fullWidth
                  label="City"
                  value={createForm.city}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, city: e.target.value })
                  }
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  fullWidth
                  label="State or Territory"
                  value={createForm.state}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, state: e.target.value })
                  }
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  fullWidth
                  label="ZIP or Postal Code"
                  value={createForm.zip}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, zip: e.target.value })
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>
            )}
          </Box>

          <Divider />

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 3,
              pt: 2,
              backgroundColor: "grey.50",
            }}
          >
            {step.name === "suggestion" && (
              <>
                <Button variant="outlined" onClick={backToList} disabled={loading}>
                  No, search instead
                </Button>
                <Button
                  variant="contained"
                  onClick={() => saveCustomer(step.customerId)}
                  disabled={loading}
                >
                  Yes, use this customer
                </Button>
              </>
            )}

            {step.name === "list" && (
              <Button variant="outlined" onClick={onClose} sx={{ minWidth: 100 }}>
                Cancel
              </Button>
            )}

            {step.name === "confirm" && (
              <>
                <Button variant="outlined" onClick={backToList} disabled={loading}>
                  No, go back
                </Button>
                <Button
                  variant="contained"
                  onClick={() => saveCustomer(step.customer.customerId)}
                  disabled={loading}
                >
                  Yes, select this customer
                </Button>
              </>
            )}

            {step.name === "create" && (
              <>
                <Button variant="outlined" onClick={backToList} disabled={loading}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={createCustomer}
                  disabled={loading || !createForm.customerName.trim()}
                >
                  Create Customer
                </Button>
              </>
            )}
          </Box>
        </Paper>
      </Box>
    </Modal>
  );
};

export default SelectSageCustomerModal;
